import { useReactiveVar } from '@apollo/client';
import { useParams } from '@tanstack/react-router';
import type React from 'react';
import { useContext } from 'react';
import { ActionDialogContext } from 'src/context/ActionDialogContext';
import { removeItemFromCollection } from 'src/dynamoQueries/collections';
import { useBuildMutation } from 'src/hooks/build';
import { useCustomToast } from 'src/hooks/menu';
import viewStateVar from 'src/store/viewState';
import LoadingDialog from '../components/dialogs/LoadingDialog';
import RemoveItemDialog from '../components/dialogs/RemoveDialog';

interface RemoveGalleryItemProps {
  isOpen: boolean;
  onClose: () => void;
}

interface RemoveItemMutationResult {
  removeItemFromCollection: {
    success: boolean;
  };
}

export default function RemoveGalleryItem({ isOpen, onClose }: RemoveGalleryItemProps): React.JSX.Element {
  const params = useParams({ strict: false });
  const galleryId = (params as { galleryId?: string }).galleryId;
  const viewState = useReactiveVar(viewStateVar);
  const { selectedAssets: assetIds } = viewState;
  const { refetch } = useContext(ActionDialogContext);
  const { toastr } = useCustomToast();

  const [mutate, { loading, error }] = useBuildMutation<RemoveItemMutationResult>(
    removeItemFromCollection(true) as unknown as Record<string, unknown>,
    {
      variables: { collectionId: galleryId, assetIds },
    },
  );

  const onClickDelete = () => {
    mutate().then(() => {
      if (refetch) {
        refetch();
      }
      onClose();
    });
  };

  if (error) {
    onClose();
    toastr.error(`Error: ${error.message}`);
  }

  if (loading) {
    return <LoadingDialog header={'Removing a gallery ...'} />;
  }

  return <RemoveItemDialog isOpen={isOpen} onClose={onClose} onDelete={onClickDelete} assetIds={assetIds} />;
}
